import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI } from '../lib/api';
import { formatDateTime, getStatusLabel, getStatusClass } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, User, Search, FileText } from 'lucide-react';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isClient, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      if (!isClient) {
        navigate('/pro/dashboard');
        return;
      }
    }
  }, [isAuthenticated, isClient, authLoading, navigate]);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await bookingsAPI.getAll();
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isClient) {
      fetchBookings();
    }
  }, [isClient]);

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) return;
    
    try {
      await bookingsAPI.updateStatus(bookingId, 'cancelled');
      setBookings(bookings.map(b => 
        b.id === bookingId ? { ...b, status: 'cancelled' } : b
      ));
      toast.success('Réservation annulée');
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending' || b.status === 'accepted');
  const pastBookings = bookings.filter(b => b.status === 'completed' || b.status === 'cancelled' || b.status === 'rejected');

  if (authLoading || !isClient) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen py-8" data-testid="client-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl text-slate-900 mb-1" style={{ fontFamily: '"DM Serif Display", serif' }}>
              Bonjour, {user?.first_name} !
            </h1>
            <p className="text-slate-600">Gérez vos réservations et trouvez des professionnels</p>
          </div>
          <Link to="/search">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-6 h-12 btn-press" data-testid="search-pro-btn">
              <Search className="h-5 w-5 mr-2" />
              Trouver un pro
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingBookings.length}</p>
                  <p className="text-sm text-slate-600">Réservations en cours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {bookings.filter(b => b.status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-600">Réservations terminées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Set(bookings.map(b => b.professional_id)).size}
                  </p>
                  <p className="text-sm text-slate-600">Professionnels contactés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Bookings */}
        <Card className="border-slate-100 mb-8">
          <CardHeader>
            <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
              Réservations en cours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <div key={i} className="h-24 rounded-lg skeleton-loading"></div>
                ))}
              </div>
            ) : pendingBookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-600 mb-4">Aucune réservation en cours</p>
                <Link to="/search">
                  <Button variant="outline" className="rounded-full" data-testid="empty-search-btn">
                    Trouver un professionnel
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100"
                    data-testid={`booking-card-${booking.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">{booking.professional_name}</h3>
                        <Badge className={getStatusClass(booking.status)}>
                          {getStatusLabel(booking.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-orange-600 mb-2">{booking.professional_trade}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatDateTime(booking.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {booking.address}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/professional/${booking.professional_id}`}>
                        <Button variant="outline" className="rounded-full" size="sm" data-testid={`view-pro-${booking.id}`}>
                          Voir le profil
                        </Button>
                      </Link>
                      {booking.status === 'pending' && (
                        <Button
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                          size="sm"
                          onClick={() => handleCancelBooking(booking.id)}
                          data-testid={`cancel-booking-${booking.id}`}
                        >
                          Annuler
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pastBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl bg-slate-50/50 border border-slate-100"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium text-slate-700">{booking.professional_name}</h3>
                        <Badge className={getStatusClass(booking.status)}>
                          {getStatusLabel(booking.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{booking.professional_trade}</p>
                      <p className="text-sm text-slate-400">{formatDateTime(booking.scheduled_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientDashboard;
